import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  EDI_TEMPLATE,
  getExplorerLoops,
  loadSegmentVisibility,
  saveSegmentVisibility,
  areOptionalSegmentVisibilitiesEqual,
  mapExplorerResponse,
  applyFhirMappingOverrides,
  getSegmentVisibilityFromTemplate,
  buildOverridesPayload,
  buildFhirMappingOverrides,
  CODE_SETS,
  collectClientConfigSamplesFromTemplate,
  areClientConfigSamplesEqual,
  arePathEditsEqual,
  hasInvalidClientConfigSamples,
  applyClientConfigSampleChange,
  loadClientConfigSamplesLocal,
  saveClientConfigSamplesLocal,
} from "./data/ediHelpers";
import { EdiConfigApi } from "../../service/Api/EdiConfigApi";
import { isPriorAuthUnauthorized } from "../../service/PriorAuthHttp";
import MappingExplorerTree from "./components/MappingExplorerTree";
import MappingExplorerPanel from "./components/MappingExplorerPanel";
import EdiPreviewDrawer from "./components/EdiPreviewDrawer";
import FhirPreviewDrawer from "./components/FhirPreviewDrawer";
import "./ediInspector.scss";

const SAVE_TOAST_MS = 4000;

const SAVE_TOAST_MESSAGES = {
  saved: {
    message: "Configuration saved successfully.",
    variant: "success",
  },
  "saved-local": {
    message:
      "Saved locally. Start prior-auth-service to persist to Overrides.json.",
    variant: "success",
  },
  "paths-empty": {
    message:
      "Path edits did not produce fhirMappings to save. Check that Dynamic fields have a binding.",
    variant: "warning",
  },
  unauthorized: {
    message:
      "Session expired or not authorized. Sign in again to load or save X12 configuration.",
    variant: "danger",
  },
  error: {
    message: "Could not save configuration.",
    variant: "danger",
  },
};

const buildMockTemplate = () => ({
  loops: getExplorerLoops(EDI_TEMPLATE, loadSegmentVisibility(EDI_TEMPLATE)),
});

const templateForLocalSave = (template, usingMockFallback) =>
  usingMockFallback ? EDI_TEMPLATE : { loops: template.loops };

const EdiInspector = () => {
  const [template, setTemplate] = useState(null);
  const [existingOverrides, setExistingOverrides] = useState(null);
  const [segmentVisibility, setSegmentVisibility] = useState({});
  const [savedVisibility, setSavedVisibility] = useState({});
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [saveToastConfig, setSaveToastConfig] = useState({
    message: "",
    variant: "success",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiConnected, setApiConnected] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [codeSets, setCodeSets] = useState(CODE_SETS);
  const [clientConfigSamples, setClientConfigSamples] = useState({});
  const [savedClientConfigSamples, setSavedClientConfigSamples] = useState({});
  const [pathEdits, setPathEdits] = useState({});
  const [savedPathEdits, setSavedPathEdits] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fhirPreviewOpen, setFhirPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("generated");
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [expandedLoops, setExpandedLoops] = useState(new Set());

  const applyLoadedTemplate = useCallback((mapped, overridesData) => {
    const withOverrides = applyFhirMappingOverrides(mapped, overridesData);
    const visibility = getSegmentVisibilityFromTemplate(withOverrides);
    const samples = collectClientConfigSamplesFromTemplate(withOverrides);
    setTemplate(withOverrides);
    setExistingOverrides(overridesData);
    setSegmentVisibility(visibility);
    setSavedVisibility({ ...visibility });
    setClientConfigSamples(samples);
    setSavedClientConfigSamples({ ...samples });
    setPathEdits({});
    setSavedPathEdits({});
    setCodeSets(withOverrides.codeSets || CODE_SETS);
  }, []);

  const showSaveFeedback = useCallback((status) => {
    const config = SAVE_TOAST_MESSAGES[status] ?? SAVE_TOAST_MESSAGES.error;
    setSaveToastConfig(config);
    setShowSaveToast(true);
  }, []);

  const loadExplorerData = useCallback(async () => {
    setIsLoading(true);
    setAuthFailed(false);

    try {
      // Bearer + ClientId come from PriorAuthHttp (OIDC access token).
      const explorerData = await EdiConfigApi.getExplorer();
      let overridesData = { overrides: {} };

      try {
        overridesData = await EdiConfigApi.getOverrides();
      } catch (overridesError) {
        if (isPriorAuthUnauthorized(overridesError)) {
          throw overridesError;
        }
        // Overrides file may not exist yet for this client.
      }

      const mapped = mapExplorerResponse(explorerData);
      applyLoadedTemplate(mapped, overridesData);
      setApiConnected(true);
    } catch (error) {
      setApiConnected(false);

      if (isPriorAuthUnauthorized(error)) {
        setAuthFailed(true);
        setTemplate(null);
        setExistingOverrides(null);
        showSaveFeedback("unauthorized");
        return;
      }

      const mockTemplate = buildMockTemplate();
      const visibility = getSegmentVisibilityFromTemplate(mockTemplate);
      const localSamples = loadClientConfigSamplesLocal();
      const baseSamples = collectClientConfigSamplesFromTemplate(mockTemplate);
      const samples = { ...baseSamples, ...localSamples };

      setTemplate(mockTemplate);
      setExistingOverrides(null);
      setSegmentVisibility(visibility);
      setSavedVisibility({ ...visibility });
      setClientConfigSamples(samples);
      setSavedClientConfigSamples({ ...samples });
      setPathEdits({});
      setSavedPathEdits({});
      setCodeSets(CODE_SETS);
    } finally {
      setIsLoading(false);
    }
  }, [applyLoadedTemplate, showSaveFeedback]);

  useEffect(() => {
    loadExplorerData();
  }, [loadExplorerData]);

  const hasUnsavedChanges = useMemo(() => {
    if (!template) return false;
    const visibilityDirty = !areOptionalSegmentVisibilitiesEqual(
      template,
      segmentVisibility,
      savedVisibility
    );
    const samplesDirty = !areClientConfigSamplesEqual(
      clientConfigSamples,
      savedClientConfigSamples
    );
    const pathsDirty = !arePathEditsEqual(pathEdits, savedPathEdits);
    return visibilityDirty || samplesDirty || pathsDirty;
  }, [
    template,
    segmentVisibility,
    savedVisibility,
    clientConfigSamples,
    savedClientConfigSamples,
    pathEdits,
    savedPathEdits,
  ]);

  const clientConfigSamplesInvalid = useMemo(() => {
    if (!template) return false;
    return hasInvalidClientConfigSamples(template, clientConfigSamples);
  }, [template, clientConfigSamples]);

  const explorerLoops = useMemo(() => {
    if (!template?.loops) return [];

    return template.loops.map((loop) => ({
      ...loop,
      segments: loop.segments.map((seg) => ({
        ...seg,
        isUserVisible:
          seg.isOptional === true
            ? segmentVisibility[seg.segmentId] !== false
            : true,
        raw:
          seg.isOptional === true && segmentVisibility[seg.segmentId] === false
            ? ""
            : seg.raw,
      })),
    }));
  }, [template, segmentVisibility]);

  const selectedSegment = useMemo(() => {
    if (!selectedSegmentId || explorerLoops.length === 0) return null;
    for (const loop of explorerLoops) {
      const seg = loop.segments.find((s) => s.segmentId === selectedSegmentId);
      if (seg) {
        return {
          ...seg,
          loopId: loop.loopId,
          loopName: loop.displayName,
        };
      }
    }
    return null;
  }, [explorerLoops, selectedSegmentId]);

  useEffect(() => {
    if (explorerLoops.length > 0) {
      setExpandedLoops(new Set(explorerLoops.map((l) => l.loopId)));
    }
  }, [explorerLoops]);

  useEffect(() => {
    if (selectedSegmentId && selectedSegment) return;
    const first = explorerLoops[0]?.segments[0];
    if (first) setSelectedSegmentId(first.segmentId);
  }, [explorerLoops, selectedSegmentId, selectedSegment]);

  const handleToggleLoop = useCallback((loopId) => {
    setExpandedLoops((prev) => {
      const next = new Set(prev);
      if (next.has(loopId)) next.delete(loopId);
      else next.add(loopId);
      return next;
    });
  }, []);

  const handleSelectSegment = useCallback((segment) => {
    setSelectedSegmentId(segment?.segmentId ?? null);
  }, []);

  const handleToggleSegmentVisibility = useCallback((segmentId, visible) => {
    setSegmentVisibility((prev) => ({ ...prev, [segmentId]: visible }));
    setShowSaveToast(false);
  }, []);

  const handleClientConfigSampleChange = useCallback(
    (elementId, value) => {
      setClientConfigSamples((prev) =>
        applyClientConfigSampleChange(template, prev, elementId, value)
      );
      setShowSaveToast(false);
    },
    [template]
  );

  const handleFhirPathChange = useCallback(
    (elementId, fhirPath, baseline = "") => {
      setPathEdits((prev) => {
        const next = { ...prev };
        if (String(fhirPath ?? "") === String(baseline ?? "")) {
          delete next[elementId];
        } else {
          next[elementId] = fhirPath;
        }
        return next;
      });
      setShowSaveToast(false);
    },
    []
  );

  const saveLocally = useCallback(
    (usingMockFallback, { markPathsSaved = false } = {}) => {
      const localTemplate = templateForLocalSave(template, usingMockFallback);
      const result = saveSegmentVisibility(localTemplate, segmentVisibility);
      if (!result.ok) {
        throw new Error("Local save failed");
      }
      const samplesResult = saveClientConfigSamplesLocal(clientConfigSamples);
      if (!samplesResult.ok) {
        throw new Error("Local sample save failed");
      }
      setSavedVisibility({ ...segmentVisibility });
      setSavedClientConfigSamples({ ...clientConfigSamples });
      if (markPathsSaved) {
        setSavedPathEdits({ ...pathEdits });
      }
      showSaveFeedback("saved-local");
    },
    [template, segmentVisibility, clientConfigSamples, pathEdits, showSaveFeedback]
  );

  const handleSaveConfig = useCallback(async () => {
    if (!template || !hasUnsavedChanges || clientConfigSamplesInvalid) return;

    setIsSaving(true);
    const usingMockFallback = !apiConnected;
    const pathsDirty = !arePathEditsEqual(pathEdits, savedPathEdits);

    try {
      if (apiConnected) {
        const payload = buildOverridesPayload(
          template,
          segmentVisibility,
          existingOverrides,
          clientConfigSamples,
          savedClientConfigSamples,
          pathEdits,
          savedPathEdits
        );

        if (pathsDirty) {
          const fhirList = buildFhirMappingOverrides(template, pathEdits);
          if (!fhirList || fhirList.length === 0) {
            showSaveFeedback("paths-empty");
            return;
          }
        }

        const result = await EdiConfigApi.saveOverrides(payload);
        const savedDoc = result.saved ?? result.document ?? payload;
        setExistingOverrides(savedDoc);
        setSavedVisibility({ ...segmentVisibility });
        setSavedClientConfigSamples({ ...clientConfigSamples });

        try {
          const explorerData = await EdiConfigApi.getExplorer();
          let mapped = mapExplorerResponse(explorerData);
          mapped = applyFhirMappingOverrides(mapped, savedDoc);
          const samples = collectClientConfigSamplesFromTemplate(mapped);
          setTemplate(mapped);
          setClientConfigSamples(samples);
          setSavedClientConfigSamples({ ...samples });
          setPathEdits({});
          setSavedPathEdits({});
          setCodeSets(mapped.codeSets || CODE_SETS);
        } catch (reloadError) {
          if (isPriorAuthUnauthorized(reloadError)) {
            throw reloadError;
          }
          const stamped = applyFhirMappingOverrides(template, savedDoc);
          setTemplate(stamped);
          setPathEdits({});
          setSavedPathEdits({});
        }

        showSaveFeedback("saved");
        return;
      }

      saveLocally(usingMockFallback, { markPathsSaved: false });
      if (pathsDirty) {
        showSaveFeedback("error");
      }
    } catch (error) {
      if (isPriorAuthUnauthorized(error)) {
        setAuthFailed(true);
        setApiConnected(false);
        showSaveFeedback("unauthorized");
        return;
      }
      if (pathsDirty) {
        showSaveFeedback("error");
      } else {
        try {
          saveLocally(usingMockFallback, { markPathsSaved: false });
        } catch {
          showSaveFeedback("error");
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    template,
    hasUnsavedChanges,
    clientConfigSamplesInvalid,
    apiConnected,
    segmentVisibility,
    existingOverrides,
    clientConfigSamples,
    savedClientConfigSamples,
    pathEdits,
    savedPathEdits,
    saveLocally,
    showSaveFeedback,
  ]);

  return (
    <>
      <ToastContainer position="top-end" className="p-3 edi-save-toast-container">
        <Toast
          show={showSaveToast}
          onClose={() => setShowSaveToast(false)}
          delay={SAVE_TOAST_MS}
          autohide
          bg={saveToastConfig.variant}
          className="w-auto text-white"
        >
          <Toast.Body role={saveToastConfig.variant === "danger" ? "alert" : "status"}>
            {saveToastConfig.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="main-content edi-mapping-explorer">
        <div className="content-header explorer-page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="explorer-page-heading">
            <div className="explorer-page-title-row">
              <h1 className="page-header mb-0">EDI Mapping Explorer</h1>
              <i
                className="fh_info_fill explorer-page-info-icon"
                data-tooltip-id="edi-explorer-info-tooltip"
                data-tooltip-content="Understand how a FHIR Prior Authorization request maps to X12 278 (005010X217) before submission"
                data-tooltip-place="bottom"
                aria-label="About EDI Mapping Explorer"
                role="img"
              />
              <Tooltip
                id="edi-explorer-info-tooltip"
                place="bottom"
                className="explorer-page-info-tooltip"
              />
            </div>
          </div>
          <div className="explorer-page-meta d-flex align-items-center gap-2 flex-wrap">
            {!isLoading && authFailed && (
              <span
                className="explorer-meta-pill muted"
                title="Sign in again to authorize X12 admin API calls."
              >
                Authentication required
              </span>
            )}
            {!isLoading && !apiConnected && !authFailed && (
              <span
                className="explorer-meta-pill muted"
                title="Unable to reach the configuration service. Built-in sample data is shown so you can explore mappings."
              >
                Showing sample configuration
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="explorer-save-status unsaved">Unsaved changes</span>
            )}
            <button
              type="button"
              className="btn btn-border-primary h-48"
              onClick={() => setFhirPreviewOpen(true)}
              disabled={isLoading}
              title="Preview sample FHIR Prior Authorization Bundle"
            >
              FHIR Preview
            </button>
            <button
              type="button"
              className="btn btn-border-primary h-48"
              onClick={() => setPreviewOpen(true)}
              disabled={isLoading || !template}
              title="Preview full X12 278 transaction"
            >
              EDI Preview
            </button>
            <button
              type="button"
              className="btn btn-primary h-48"
              onClick={handleSaveConfig}
              disabled={
                !hasUnsavedChanges ||
                isSaving ||
                isLoading ||
                clientConfigSamplesInvalid
              }
              title={
                clientConfigSamplesInvalid
                  ? "Fix invalid client configuration sample lengths before saving"
                  : hasUnsavedChanges
                    ? apiConnected
                      ? "Save segment visibility, client configuration samples, and FHIR path edits"
                      : "Save locally until the configuration service is available"
                    : "Change an optional segment, client configuration sample, or FHIR path first"
              }
            >
              {isSaving ? "Saving…" : "Save Configuration"}
            </button>
          </div>
        </div>

        <div className="card edi-explorer-card">
          <div className="card-body">
            {isLoading ? (
              <div className="explorer-panel explorer-panel-empty">
                <p>Loading X12 configuration…</p>
              </div>
            ) : authFailed ? (
              <div className="explorer-panel explorer-panel-empty">
                <p>
                  Unable to load X12 configuration. Your session may have expired —
                  sign in again and retry.
                </p>
              </div>
            ) : (
              <div className="explorer-layout">
                <aside className="explorer-layout-left">
                  <MappingExplorerTree
                    loops={explorerLoops}
                    selectedSegment={selectedSegment}
                    expandedLoops={expandedLoops}
                    onToggleLoop={handleToggleLoop}
                    onSelectSegment={handleSelectSegment}
                    onToggleSegmentVisibility={handleToggleSegmentVisibility}
                  />
                </aside>

                <main className="explorer-layout-right">
                  <MappingExplorerPanel
                    segment={selectedSegment}
                    onToggleSegmentVisibility={handleToggleSegmentVisibility}
                    codeSets={codeSets}
                    clientConfigSamples={clientConfigSamples}
                    onClientConfigSampleChange={handleClientConfigSampleChange}
                    pathEdits={pathEdits}
                    onFhirPathChange={handleFhirPathChange}
                  />
                </main>
              </div>
            )}
          </div>
        </div>

        <EdiPreviewDrawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          loops={explorerLoops}
          selectedSegmentId={selectedSegment?.segmentId}
          previewMode={previewMode}
          onPreviewModeChange={setPreviewMode}
        />

        <FhirPreviewDrawer
          open={fhirPreviewOpen}
          onClose={() => setFhirPreviewOpen(false)}
          selectedSegmentId={selectedSegment?.segmentId}
        />
      </div>
    </>
  );
};

export default EdiInspector;
